import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, Link as LinkIcon, Copy, CheckCircle2,
  Trash2, ChevronRight,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { usePreferences } from '../hooks/usePreferences'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { themes, applyTheme, AVAILABLE_THEMES } from '../lib/themes'
import { generateInviteToken } from '../lib/inviteToken'
import { track } from '../lib/posthog'

// Stripe subscription state shape returned by the subscription-status edge
// function. `status === 'free'` short-circuits before the Stripe API call;
// `unknown` is the fallback if Stripe rejects the lookup. Other values are
// the raw Stripe.Subscription.status enum.
type SubscriptionState =
  | { status: 'free' | 'unknown' }
  | {
      status:               'trialing' | 'active' | 'canceled' | 'past_due' | 'incomplete' | 'incomplete_expired' | 'unpaid' | 'paused'
      cancel_at_period_end: boolean
      trial_end:            number | null
      current_period_end:   number
      unit_amount:          number
      currency:             string
      interval:             'month' | 'year' | 'week' | 'day'
    }

function formatShortDate(unixSeconds: number) {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const THEME_NAMES = Object.keys(themes)

// ─── Shared UI ────────────────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children }: {
  title:    string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="mb-1">
      <div className="px-1 mb-2">
        <h2 className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
          {title}
        </h2>
        {subtitle && (
          <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.4]">
            {subtitle}
          </p>
        )}
      </div>
      <div className="rounded-[18px] border border-border-sand bg-paper overflow-hidden">
        {children}
      </div>
    </div>
  )
}

function Toggle({ on, onChange, disabled = false }: {
  on: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={() => { if (!disabled) onChange(!on) }}
      aria-pressed={on}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        on ? 'bg-forest' : 'bg-border-avatar'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-paper shadow-sm transition-transform duration-200 ${
          on ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  )
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export default function Settings() {
  const navigate    = useNavigate()
  const { user }    = useAuth()
  const {
    theme:                  savedTheme,
    compactMode:            savedCompactMode,
    fontSize:               savedFontSize,
    defaultTrackExpiration: savedDefaultTrackExpiration,
    autoAddToListOnZero:    savedAutoAddToListOnZero,
    savePreferences,
  } = usePreferences()

  const {
    isSubscribed:       pushSubscribed,
    isSupported:        pushSupported,
    permission:         pushPermission,
    subscribe:          subscribePush,
    unsubscribe:        unsubscribePush,
    isLoading:          pushLoading,
    error:              pushError,
  } = usePushSubscription()

  const queryClient = useQueryClient()

  const [selectedTheme,          setSelectedTheme]          = useState(savedTheme)
  const [compactMode,            setCompactMode]            = useState(savedCompactMode)
  const [fontSize,               setFontSize]               = useState(savedFontSize)
  const [defaultTrackExpiration, setDefaultTrackExpiration] = useState(savedDefaultTrackExpiration)
  const [autoAddOnZero,          setAutoAddOnZero]          = useState(savedAutoAddToListOnZero)
  const [householdId,            setHouseholdId]            = useState<string | null>(null)
  const [toast,                  setToast]                  = useState<string | null>(null)
  const [showClearModal,         setShowClearModal]         = useState(false)
  const [clearing,               setClearing]               = useState(false)
  const [showCancelSubModal,     setShowCancelSubModal]     = useState(false)
  const [cancelingSub,           setCancelingSub]           = useState(false)

  // Founding-member flag — separate query key from Household's userRow so
  // a future schema change to userRow doesn't drift Settings's expected
  // shape. Cheap call, dedupes naturally if the user navigates between
  // Household and Settings within the staleTime window.
  const { data: foundingRow } = useQuery({
    queryKey:  ['user-founding', user?.id],
    enabled:   !!user?.id,
    staleTime: 60_000,
    queryFn:   async () => {
      const { data } = await supabase
        .from('users')
        .select('is_founding_member, founding_member_since')
        .eq('id', user!.id)
        .single()
      return data as { is_founding_member: boolean | null; founding_member_since: string | null } | null
    },
  })
  const isFounding = foundingRow?.is_founding_member === true

  // Realtime: keep the user row + members list fresh when another member
  // joins, leaves, or updates a relevant field. Settings reads userRow,
  // user-founding, and householdMembers — all derived from `users`, so a
  // single subscription invalidates them together.
  useHouseholdRealtime(householdId, {
    tables: ['users'],
    invalidateMap: {
      users: [['userRow', user?.id], ['user-founding', user?.id], ['householdMembers', householdId]],
    },
  })

  // Subscription status — fetched via edge function because users table
  // doesn't cache trial_end / current_period_end / cancel_at_period_end.
  // staleTime of 60s prevents re-fetches on every Settings re-render.
  const { data: sub, isLoading: subLoading } = useQuery<SubscriptionState>({
    queryKey: ['subscriptionStatus', user?.id],
    enabled:  !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return { status: 'free' }
      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      )
      if (!resp.ok) return { status: 'unknown' }
      return resp.json()
    },
  })

  // ── Invite + members state ─────────────────────────────────────────────────
  const [inviteLink,       setInviteLink]       = useState<string | null>(null)
  const [generatingInvite, setGeneratingInvite] = useState(false)
  const [householdMembers, setHouseholdMembers] = useState<{
    id: string; display_name: string | null; email: string | null
  }[]>([])

  useEffect(() => { document.title = 'Settings — Pantry' }, [])

  useEffect(() => { setSelectedTheme(savedTheme)                           }, [savedTheme])
  useEffect(() => { setCompactMode(savedCompactMode)                       }, [savedCompactMode])
  useEffect(() => { setFontSize(savedFontSize)                             }, [savedFontSize])
  useEffect(() => { setDefaultTrackExpiration(savedDefaultTrackExpiration) }, [savedDefaultTrackExpiration])
  useEffect(() => { setAutoAddOnZero(savedAutoAddToListOnZero)             }, [savedAutoAddToListOnZero])

  useEffect(() => {
    if (!user) return
    supabase.from('users').select('household_id').eq('id', user.id).single()
      .then(({ data }) => {
        if (!data) return
        setHouseholdId(data.household_id)
        supabase
          .from('users')
          .select('id, display_name, email')
          .eq('household_id', data.household_id)
          .then(({ data: members }) => {
            if (members) setHouseholdMembers(members)
          })
      })
  }, [user])

  // Realtime: when another user's household_id flips to ours (an invite
  // accept), or someone leaves, refetch the members list. Settings doesn't
  // use React Query for this, so we re-run the same fetch directly into
  // local state. Same publication requirement as Household.tsx.
  useEffect(() => {
    if (!householdId) return
    const channel = supabase
      .channel(`settings-members-${householdId}`)
      .on(
        'postgres_changes',
        {
          event:  '*',
          schema: 'public',
          table:  'users',
          filter: `household_id=eq.${householdId}`,
        },
        () => {
          supabase
            .from('users')
            .select('id, display_name, email')
            .eq('household_id', householdId)
            .then(({ data: members }) => {
              if (members) setHouseholdMembers(members)
            })
        }
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [householdId])

  // ── Helpers ────────────────────────────────────────────────────────────────

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  function save(partial: Parameters<typeof savePreferences>[0]) {
    savePreferences(partial)
    showToast('Saved')
  }

  // ── Theme ─────────────────────────────────────────────────────────────────

  function handleThemeSelect(t: string) {
    setSelectedTheme(t)
    applyTheme(t)
    save({ theme: t })
  }

  // ── Display ───────────────────────────────────────────────────────────────

  function handleCompactToggle(v: boolean) {
    setCompactMode(v)
    document.documentElement.classList.toggle('compact-mode', v)
    save({ compactMode: v })
  }

  function handleFontSizeSelect(s: string) {
    setFontSize(s)
    const sizes: Record<string, string> = { small: '14px', normal: '16px', large: '18px' }
    document.documentElement.style.setProperty('--font-size-base', sizes[s] ?? '16px')
    save({ fontSize: s })
  }

  function handleDefaultTrackExpirationToggle(v: boolean) {
    setDefaultTrackExpiration(v)
    save({ defaultTrackExpiration: v })
  }

  function handleAutoAddOnZeroToggle(v: boolean) {
    setAutoAddOnZero(v)
    save({ autoAddToListOnZero: v })
  }

  // ── Notifications ─────────────────────────────────────────────────────────
  // Hook surfaces errors via pushError; swallow the throw here so the toggle
  // doesn't blow up the page. The user-facing message renders below the row.

  async function handlePushToggle(v: boolean) {
    try {
      if (v) await subscribePush()
      else   await unsubscribePush()
    } catch {
      /* error displayed inline via pushError */
    }
  }

  // ── Generate invite link ───────────────────────────────────────────────────

  async function handleGenerateInvite() {
    setGeneratingInvite(true)
    try {
      // Read the canonical user and household_id from the SAME sources the RLS policy
      // uses (auth.uid() and get_my_household_id()). This eliminates any class of bug
      // where the client's cached householdId drifts from what the policy evaluates.
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) throw new Error('Not signed in')

      const { data: resolvedHouseholdId, error: rpcErr } = await supabase.rpc('get_my_household_id')
      if (rpcErr) throw rpcErr
      if (!resolvedHouseholdId) throw new Error('No household found for this account')

      const token     = generateInviteToken()
      const expiresAt = new Date(Date.now() + 7 * 86_400_000).toISOString()

      const { data, error } = await supabase
        .from('household_invites')
        .insert({
          household_id: resolvedHouseholdId,
          created_by:   authUser.id,
          token,
          expires_at:   expiresAt,
          used:         false,
        })
        .select('token')
        .single()

      if (error) throw error

      track('household_invited')
      setInviteLink(`${window.location.origin}/join/${data.token}`)
    } catch (err: any) {
      // Supabase returns { message, details, hint, code } — surface the real message
      // so future failures aren't hidden behind generic copy.
      console.error('Invite error:', err)
      const message =
        err?.message ??
        err?.error_description ??
        err?.details ??
        'Could not generate invite link'
      showToast(message)
    } finally {
      setGeneratingInvite(false)
    }
  }

  async function handleCopyInvite() {
    if (!inviteLink) return
    try {
      await navigator.clipboard.writeText(inviteLink)
      showToast('Invite link copied! 🔗')
    } catch {
      showToast('Long press the link to copy it manually')
    }
  }

  // ── Cancel subscription ───────────────────────────────────────────────────

  async function handleCancelSubscription() {
    setCancelingSub(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not signed in')

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type':  'application/json',
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      )

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.error || 'Cancel failed')
      }

      const data = await resp.json() as { ok: boolean; ends_at: number }
      queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] })
      queryClient.invalidateQueries({ queryKey: ['userRow'] })
      setShowCancelSubModal(false)
      showToast(`Canceled. You'll keep access until ${formatShortDate(data.ends_at)}.`)
    } catch (err) {
      console.error('Cancel subscription error:', err)
      showToast("Couldn't cancel — try again or contact support.")
    } finally {
      setCancelingSub(false)
    }
  }

  // ── Danger zone ───────────────────────────────────────────────────────────

  async function handleClearData() {
    if (!householdId || !user) return
    setClearing(true)
    try {
      const { data: lists } = await supabase
        .from('shopping_lists').select('id').eq('household_id', householdId)
      const listIds = (lists ?? []).map(l => l.id)
      if (listIds.length > 0) {
        await supabase.from('list_items').delete().in('list_id', listIds)
      }
      await supabase.from('shopping_lists').delete().eq('household_id', householdId)
      await supabase.from('confirmations').delete().eq('household_id', householdId)
      await supabase.from('pantry_items').delete().eq('household_id', householdId)
      await supabase.auth.signOut()
      navigate('/sign-in')
    } catch (err) {
      console.error('Clear data failed:', err)
      showToast('Something went wrong')
    } finally {
      setClearing(false)
      setShowClearModal(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <main className="min-h-[100dvh] bg-cream flex justify-center">
      <div className="w-full max-w-[480px] pb-16">

        {/* Back bar */}
        <div className="flex items-center px-4 pt-4 pb-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 py-1 text-[13px] font-medium text-ink-muted"
          >
            <ChevronLeft className="h-[14px] w-[14px]" strokeWidth={1.7} />
            Back
          </button>
        </div>

        {/* Masthead */}
        <header className="px-4 pb-5 mb-4 border-b border-border-sand">
          <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            Customize
          </div>
          <h1 className="font-serif text-[28px] font-normal leading-[1.05] tracking-[-0.015em] text-ink">
            Your <em className="italic">settings</em>
          </h1>
          <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.4]">
            Small tweaks to make Pantry feel like yours.
          </p>
        </header>

        <div className="flex flex-col gap-5 px-4">

          {/* ── SUBSCRIPTION ── */}
          {/* Four states based on Stripe subscription:
              free | trialing | active | canceled-still-in-period.
              `unknown` (Stripe API down) renders as free so the UI never
              shows a partial "you might be subscribed" state. */}

          <SectionCard title="Subscription">
            <div className="px-5 py-4">
              {(() => {
                if (!sub || subLoading) {
                  return <div className="h-12 animate-pulse bg-cream-warm rounded-[12px]" />
                }
                // `in` is a reliable TS discriminator across union variants
                // here — the active-shape variant has cancel_at_period_end,
                // the free/unknown variant doesn't.
                if (!('cancel_at_period_end' in sub)) {
                  return (
                    <>
                      <p className="font-serif text-[14px] font-medium text-ink mb-1">
                        You're on the free plan.
                      </p>
                      <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                        Upgrade to Pantry+ for unlimited receipt scans, AI suggestions, and more.
                      </p>
                      <button
                        onClick={() => navigate('/paywall')}
                        className="rounded-[14px] bg-forest text-forest-ink px-4 py-2.5 text-[13px] font-bold tracking-[-0.01em]"
                      >
                        Upgrade to Pantry+
                      </button>
                    </>
                  )
                }
                if (sub.cancel_at_period_end) {
                  return (
                    <>
                      <p className="font-serif text-[14px] font-medium text-ink mb-1">
                        Canceled · access until {formatShortDate(sub.current_period_end)}
                      </p>
                      <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                        You won't be charged again. After {formatShortDate(sub.current_period_end)}, you'll move to the free plan. Resubscribe anytime.
                      </p>
                      <button
                        onClick={() => navigate('/paywall')}
                        className="rounded-[14px] bg-forest text-forest-ink px-4 py-2.5 text-[13px] font-bold tracking-[-0.01em]"
                      >
                        Resubscribe
                      </button>
                    </>
                  )
                }
                if (sub.status === 'trialing') {
                  if (isFounding) {
                    return (
                      <>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta mb-1">
                          Pantry+ Founding · trial ends {formatShortDate(sub.trial_end ?? sub.current_period_end)}
                        </div>
                        <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                          After your trial, $24.99/yr forever
                        </p>
                        <button
                          onClick={() => setShowCancelSubModal(true)}
                          className="rounded-[14px] border border-terracotta text-terracotta px-4 py-2.5 text-[13px] font-medium"
                        >
                          Cancel subscription
                        </button>
                      </>
                    )
                  }
                  return (
                    <>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta mb-1">
                        Free trial · ends {formatShortDate(sub.trial_end ?? sub.current_period_end)}
                      </div>
                      <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                        After your trial, you'll be charged ${(sub.unit_amount / 100).toFixed(2)} for {sub.interval === 'year' ? 'an annual' : 'a monthly'} subscription.
                      </p>
                      <button
                        onClick={() => setShowCancelSubModal(true)}
                        className="rounded-[14px] border border-terracotta text-terracotta px-4 py-2.5 text-[13px] font-medium"
                      >
                        Cancel subscription
                      </button>
                    </>
                  )
                }
                if (sub.status === 'active') {
                  if (isFounding) {
                    return (
                      <>
                        <p className="font-serif text-[14px] font-medium text-ink mb-1">
                          Pantry+ Founding · renews {formatShortDate(sub.current_period_end)}
                        </p>
                        <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                          $24.99/yr forever · locked at this price for life
                        </p>
                        <button
                          onClick={() => setShowCancelSubModal(true)}
                          className="rounded-[14px] border border-terracotta text-terracotta px-4 py-2.5 text-[13px] font-medium"
                        >
                          Cancel subscription
                        </button>
                      </>
                    )
                  }
                  return (
                    <>
                      <p className="font-serif text-[14px] font-medium text-ink mb-1">
                        Pantry+ active · renews {formatShortDate(sub.current_period_end)}
                      </p>
                      <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                        ${(sub.unit_amount / 100).toFixed(2)} / {sub.interval}
                      </p>
                      <button
                        onClick={() => setShowCancelSubModal(true)}
                        className="rounded-[14px] border border-terracotta text-terracotta px-4 py-2.5 text-[13px] font-medium"
                      >
                        Cancel subscription
                      </button>
                    </>
                  )
                }
                // past_due, canceled-already-ended, paused, etc. — surface
                // the status and let the user resubscribe rather than guess.
                return (
                  <>
                    <p className="font-serif text-[14px] font-medium text-ink mb-1">
                      Subscription status: {sub.status}
                    </p>
                    <p className="font-serif text-[12px] italic text-ink-muted leading-[1.45] mb-3">
                      Resubscribe to restore access, or contact support if this looks wrong.
                    </p>
                    <button
                      onClick={() => navigate('/paywall')}
                      className="rounded-[14px] bg-forest text-forest-ink px-4 py-2.5 text-[13px] font-bold tracking-[-0.01em]"
                    >
                      Resubscribe
                    </button>
                  </>
                )
              })()}
            </div>
          </SectionCard>

          {/* ── COLORS ── */}

          <SectionCard title="Colors" subtitle="The warm editorial palette is our current look.">
            <div className="p-5">
              <div className="grid grid-cols-3 gap-2">
                {THEME_NAMES.map(name => {
                  const t       = themes[name]
                  const isAvail = (AVAILABLE_THEMES as readonly string[]).includes(name)
                  const sel     = isAvail && selectedTheme === name
                  return (
                    <button
                      key={name}
                      onClick={() => { if (isAvail) handleThemeSelect(name) }}
                      disabled={!isAvail}
                      className={`relative flex flex-col items-center gap-1.5 rounded-[14px] border p-3 transition-all text-left ${
                        sel
                          ? 'border-[1.5px] border-forest bg-ok-bg'
                          : isAvail
                          ? 'border-border-sand bg-paper'
                          : 'border-border-sand bg-paper opacity-50'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <div className="h-3 w-3 rounded-full" style={{ background: t.surface }} />
                        <div className="h-4 w-4 rounded-full" style={{ background: t.accent }} />
                        <div className="h-3 w-3 rounded-full" style={{ background: t.surface }} />
                      </div>
                      <span className={`font-serif text-[11px] ${
                        sel ? 'font-medium text-forest' : isAvail ? 'text-ink' : 'italic text-ink-muted'
                      }`}>
                        {t.name}
                      </span>
                      {sel && (
                        <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-forest">
                          <CheckCircle2 className="h-[10px] w-[10px] text-forest-ink" strokeWidth={2.5} fill="none" />
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              <p className="font-serif text-[11px] italic text-ink-muted mt-4 leading-[1.4]">
                More palettes coming soon.
              </p>
            </div>
          </SectionCard>

          {/* ── HOUSEHOLD ── */}

          <SectionCard title="Household" subtitle="Share your pantry with others.">

            {householdMembers.length > 0 && (
              <>
                <div className="px-5 pt-4 pb-2">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted">
                    Members
                  </div>
                </div>
                <div>
                  {householdMembers.map((member, idx) => (
                    <div
                      key={member.id}
                      className={`flex items-center gap-3 px-5 py-3 ${
                        idx !== householdMembers.length - 1 ? 'border-b border-border-hairline' : ''
                      }`}
                    >
                      <div className="h-9 w-9 rounded-full bg-forest text-forest-ink flex-shrink-0 flex items-center justify-center font-serif text-[14px] font-medium">
                        {(member.display_name || member.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em] truncate">
                          {member.display_name || 'No name'}
                          {member.id === user?.id && (
                            <span className="font-serif text-[12px] italic text-ink-muted ml-1.5 font-normal">(you)</span>
                          )}
                        </p>
                        <p className="font-serif text-[12px] italic text-ink-muted truncate">
                          {member.email}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={`px-5 pb-5 ${householdMembers.length > 0 ? 'pt-4 border-t border-border-hairline' : 'pt-4'}`}>
              <div className="text-[10px] uppercase tracking-[0.15em] font-medium text-ink-muted mb-3">
                Invite someone
              </div>

              {!inviteLink ? (
                <button
                  onClick={handleGenerateInvite}
                  disabled={generatingInvite}
                  className="w-full rounded-[14px] bg-forest text-forest-ink py-3 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 flex items-center justify-center gap-2 transition"
                >
                  <LinkIcon className="h-[14px] w-[14px]" strokeWidth={1.8} />
                  {generatingInvite ? 'Generating…' : 'Generate invite link'}
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="rounded-[14px] bg-cream-warm border border-border-sand px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.12em] font-medium text-ink-muted mb-1.5">
                      Share this link — expires in 7 days
                    </p>
                    <p className="font-mono text-[12px] text-ink break-all leading-relaxed select-all">
                      {inviteLink}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyInvite}
                      className="flex-1 rounded-[14px] bg-forest text-forest-ink py-2.5 text-[13px] font-bold flex items-center justify-center gap-1.5"
                    >
                      <Copy className="h-[13px] w-[13px]" strokeWidth={1.8} />
                      Copy link
                    </button>
                    <button
                      onClick={() => setInviteLink(null)}
                      className="px-4 py-2.5 rounded-[14px] text-[13px] font-semibold text-ink-muted border border-border-sand bg-paper"
                    >
                      Done
                    </button>
                  </div>
                  <p className="font-serif text-[11px] italic text-ink-muted text-center mt-1 leading-[1.45]">
                    When they open the link and sign in, they'll join your household automatically.
                  </p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── DISPLAY ── */}

          <SectionCard title="Display">
            <div>
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-hairline">
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink">Track expiration dates</p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                    New items will have expiry tracking on by default.
                  </p>
                </div>
                <Toggle on={defaultTrackExpiration} onChange={handleDefaultTrackExpirationToggle} />
              </div>

              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-hairline">
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink">Auto-suggest when items run out</p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                    When an item's quantity hits zero, ask if you'd like to add it to your shopping list.
                  </p>
                </div>
                <Toggle on={autoAddOnZero} onChange={handleAutoAddOnZeroToggle} />
              </div>

              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border-hairline">
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink">Compact list view</p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                    Show more items on screen.
                  </p>
                </div>
                <Toggle on={compactMode} onChange={handleCompactToggle} />
              </div>

              <div className="px-5 py-4">
                <p className="font-serif text-[14px] font-medium text-ink mb-3">Text size</p>
                <div className="flex gap-2">
                  {['small', 'normal', 'large'].map((s) => (
                    <button
                      key={s}
                      onClick={() => handleFontSizeSelect(s)}
                      className={`flex-1 py-2 rounded-[12px] font-serif text-[13px] capitalize transition-colors ${
                        fontSize === s
                          ? 'bg-forest text-forest-ink font-medium'
                          : 'bg-cream border border-border-sand text-ink'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ── NOTIFICATIONS ── */}

          <SectionCard title="Notifications">
            <div className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink">Expiry reminders</p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                    {!pushSupported
                      ? "Not supported on this device. iOS Safari requires the app to be added to your home screen first."
                      : pushPermission === 'denied'
                      ? "Notifications are blocked. Enable them in your browser settings."
                      : "Get notified 3 days, 1 day, and the day of expiry."}
                  </p>
                </div>
                <Toggle
                  on={pushSubscribed}
                  onChange={handlePushToggle}
                  disabled={!pushSupported || pushPermission === 'denied' || pushLoading}
                />
              </div>
              {pushError && (
                <p className="font-serif text-[12px] italic text-critical mt-2 leading-[1.4]">
                  {pushError}
                </p>
              )}
            </div>
          </SectionCard>

          {/* ── ABOUT ── */}

          <SectionCard title="About">
            <div>
              <div className="flex items-center justify-between px-5 py-4 border-b border-border-hairline">
                <span className="font-serif text-[14px] italic text-ink-muted">Version</span>
                <span className="font-serif text-[14px] font-medium text-ink">1.0.0</span>
              </div>
              <a
                href="mailto:privacy@pantryioapp.com"
                className="flex items-center justify-between px-5 py-4 border-b border-border-hairline"
              >
                <span className="font-serif text-[14px] italic text-ink-muted">Support</span>
                <span className="font-serif text-[14px] font-medium text-forest">
                  privacy@pantryioapp.com
                </span>
              </a>
              <button
                onClick={() => navigate('/privacy')}
                className="flex w-full items-center justify-between px-5 py-4 border-b border-border-hairline text-left"
              >
                <span className="font-serif text-[14px] italic text-ink-muted">Privacy policy</span>
                <ChevronRight className="h-4 w-4 text-ink-muted" strokeWidth={1.7} />
              </button>
              <button className="flex w-full items-center justify-between px-5 py-4 text-left">
                <span className="font-serif text-[14px] italic text-ink-muted">Terms of service</span>
                <ChevronRight className="h-4 w-4 text-ink-muted" strokeWidth={1.7} />
              </button>
            </div>
          </SectionCard>

          {/* ── DANGER ZONE ── */}

          <div className="mb-1">
            <div className="px-1 mb-2">
              <h2 className="text-[10px] uppercase tracking-[0.18em] font-medium text-critical">
                Danger zone
              </h2>
              <p className="font-serif text-[13px] italic text-ink-muted mt-1 leading-[1.4]">
                Permanent actions. No undo.
              </p>
            </div>
            <button
              onClick={() => setShowClearModal(true)}
              className="w-full rounded-[14px] border border-critical/30 bg-critical-bg py-3 font-serif text-[14px] italic font-medium text-critical flex items-center justify-center gap-2"
            >
              <Trash2 className="h-[14px] w-[14px]" strokeWidth={1.8} />
              Clear all data
            </button>
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate('/delete-account')}
                className="font-serif text-[12px] italic text-ink-muted underline-offset-4 hover:text-ink hover:underline"
              >
                Delete my account
              </button>
            </div>
          </div>

          {/* Legal + support links — last block on the page so they don't
              compete with primary settings actions. Apple App Review needs
              these reachable from inside the signed-in app too, not just
              from the public footer on the static pages. */}
          <nav className="flex flex-wrap justify-center gap-x-2.5 gap-y-1.5 text-[12px] text-ink-muted pt-2 pb-4">
            <button
              onClick={() => navigate('/privacy')}
              className="hover:text-ink underline-offset-2 hover:underline"
            >
              Privacy
            </button>
            <span className="text-ink-muted/40">·</span>
            <button
              onClick={() => navigate('/terms')}
              className="hover:text-ink underline-offset-2 hover:underline"
            >
              Terms
            </button>
            <span className="text-ink-muted/40">·</span>
            <button
              onClick={() => navigate('/refund-policy')}
              className="hover:text-ink underline-offset-2 hover:underline"
            >
              Refund Policy
            </button>
            <span className="text-ink-muted/40">·</span>
            <button
              onClick={() => navigate('/support')}
              className="hover:text-ink underline-offset-2 hover:underline"
            >
              Support
            </button>
          </nav>

        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="fixed top-5 left-1/2 -translate-x-1/2 z-50 rounded-[12px] bg-ink text-cream px-5 py-2.5 font-serif text-[13px] font-medium shadow-lg whitespace-nowrap"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel subscription modal */}
      <AnimatePresence>
        {showCancelSubModal && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCancelSubModal(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-[28px] bg-cream px-6 pt-3 pb-10 max-w-[480px] mx-auto"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            >
              <div className="flex justify-center pt-1 pb-4">
                <div className="h-1 w-10 rounded-full bg-border-avatar" />
              </div>

              <div className="text-center mb-5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta mb-1">
                  Cancel subscription
                </div>
                <h2 className="font-serif text-[24px] font-normal leading-[1.1] tracking-[-0.015em] text-ink">
                  Cancel <em className="italic">Pantry+</em>?
                </h2>
                <p className="font-serif text-[13px] italic text-ink-muted mt-2 leading-[1.45] max-w-[320px] mx-auto">
                  You'll keep access until {sub && 'current_period_end' in sub ? formatShortDate(sub.current_period_end) : 'the end of the period'}. After that, you'll move to the free plan. You can resubscribe anytime.
                </p>
              </div>

              <button
                onClick={() => setShowCancelSubModal(false)}
                disabled={cancelingSub}
                className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 font-bold text-[14px] tracking-[-0.01em] disabled:opacity-60"
              >
                Keep subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelingSub}
                className="w-full mt-3 py-3 rounded-[14px] border border-terracotta text-terracotta font-serif text-[13px] italic disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {cancelingSub && (
                  <span className="h-4 w-4 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
                )}
                {cancelingSub ? 'Canceling…' : 'Yes, cancel'}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Clear data modal */}
      <AnimatePresence>
        {showClearModal && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowClearModal(false)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-[28px] bg-cream px-6 pt-3 pb-10 max-w-[480px] mx-auto"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            >
              <div className="flex justify-center pt-1 pb-4">
                <div className="h-1 w-10 rounded-full bg-border-avatar" />
              </div>

              <div className="text-center mb-5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-critical mb-1">
                  Are you sure
                </div>
                <h2 className="font-serif text-[24px] font-normal leading-[1.1] tracking-[-0.015em] text-ink">
                  Delete <em className="italic">everything</em>?
                </h2>
                <p className="font-serif text-[13px] italic text-ink-muted mt-2 leading-[1.45] max-w-[320px] mx-auto">
                  This will remove all your pantry items, shopping lists, recipes, and history. It cannot be undone.
                </p>
              </div>

              <button
                onClick={handleClearData}
                disabled={clearing}
                className="w-full rounded-[14px] bg-critical text-cream py-3.5 font-bold text-[14px] tracking-[-0.01em] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {clearing && (
                  <span className="h-4 w-4 rounded-full border-2 border-cream border-t-transparent animate-spin" />
                )}
                {clearing ? 'Deleting…' : 'Delete everything'}
              </button>
              <button
                onClick={() => setShowClearModal(false)}
                className="w-full mt-3 py-3 font-serif text-[13px] italic text-ink-muted"
              >
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  )
}
